import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, Chapter } from "./Reveal";

const NODES = [
  { id: "uae", flag: "🇦🇪", name: "UAE", hub: "Dubai", note: "Luxury, MICE & stopover programs", cur: "AED", x: 169, y: 97 },
  { id: "india", flag: "🇮🇳", name: "India", hub: "Delhi NCR · Mumbai", note: "Inbound circuits & domestic leisure", cur: "INR", x: 400, y: 150 },
  { id: "thailand", flag: "🇹🇭", name: "Thailand", hub: "Bangkok", note: "Islands, city & wellness escapes", cur: "THB", x: 712, y: 231 },
  { id: "vietnam", flag: "🇻🇳", name: "Vietnam", hub: "Hanoi · HCMC", note: "Heritage trails & adventure", cur: "USD", x: 774, y: 140 },
  { id: "malaysia", flag: "🇲🇾", name: "Malaysia", hub: "Kuala Lumpur", note: "City, highlands & Borneo", cur: "MYR", x: 726, y: 367 },
  { id: "singapore", flag: "🇸🇬", name: "Singapore", hub: "Singapore", note: "Cruise & stopover gateway", cur: "SGD", x: 750, y: 389 },
  { id: "indonesia", flag: "🇮🇩", name: "Indonesia", hub: "Bali · Jakarta", note: "Resorts, dive & experiences", cur: "IDR", x: 786, y: 485 },
];

const ARCS = [
  "M169,97 Q280,10 400,150",
  "M400,150 Q560,60 712,231",
  "M712,231 Q760,180 774,140",
  "M712,231 Q700,320 726,367",
  "M726,367 Q740,380 750,389",
  "M750,389 Q780,440 786,485",
  "M169,97 Q440,260 750,389",
];

export default function GlobalMap() {
  const [active, setActive] = useState(null);
  const node = NODES.find((n) => n.id === active);

  return (
    <section id="global" className="border-y border-sand bg-parch py-24 lg:py-32">
      <div className="container-x">
        <Reveal><Chapter index={4} label="Global Footprint" /></Reveal>
        <div className="mt-8 grid gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <h2 className="font-serif text-3xl leading-[1.15] tracking-tight text-ink sm:text-4xl lg:text-5xl">
              Built for Global Destination Businesses
            </h2>
            <p className="mt-5 text-base leading-relaxed text-stone2">
              Designed for DMCs operating across destinations, currencies and
              international B2B markets.
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {NODES.map((n) => (
                <button
                  key={n.id}
                  onMouseEnter={() => setActive(n.id)}
                  onClick={() => setActive(n.id)}
                  data-testid={`map-chip-${n.id}`}
                  className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors duration-300 ${
                    active === n.id ? "border-terra bg-terra text-paper" : "border-sand bg-white text-ink hover:border-terra/50"
                  }`}
                >
                  {n.flag} {n.name}
                </button>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.12} className="lg:col-span-8">
            <div
              className="relative overflow-hidden rounded-2xl border border-sand bg-paper shadow-inner"
              data-testid="world-map-interactive"
            >
              <svg viewBox="0 0 1000 560" className="block w-full">
                <defs>
                  <pattern id="dots" width="26" height="26" patternUnits="userSpaceOnUse">
                    <circle cx="1.4" cy="1.4" r="1.4" fill="#E3DCCE" />
                  </pattern>
                </defs>
                <rect width="1000" height="560" fill="url(#dots)" />
                {/* Stylised landmasses */}
                <g fill="#EAE4D6" stroke="#DCD4C2" strokeWidth="1.5">
                  <path d="M60,40 Q120,15 190,50 Q250,80 242,160 Q232,235 160,262 Q98,272 68,210 Q38,140 60,40 Z" />
                  <path d="M298,58 Q400,18 502,50 Q562,80 522,152 Q482,222 432,302 Q410,334 398,300 Q360,222 318,160 Q276,100 298,58 Z" />
                  <path d="M640,120 Q722,78 802,122 Q862,162 842,242 Q822,322 760,362 Q700,392 660,330 Q620,260 630,180 Q632,148 640,120 Z" />
                  <path d="M690,342 Q732,330 752,372 Q772,412 756,432 Q734,442 714,406 Q688,372 690,342 Z" />
                  <ellipse cx="700" cy="470" rx="60" ry="22" transform="rotate(-18 700 470)" />
                  <ellipse cx="800" cy="500" rx="70" ry="18" transform="rotate(-8 800 500)" />
                  <ellipse cx="880" cy="470" rx="42" ry="30" />
                  <ellipse cx="940" cy="510" rx="46" ry="14" />
                  <ellipse cx="950" cy="150" rx="30" ry="44" />
                </g>
                {/* Routes */}
                <g fill="none" stroke="#C85A32" strokeWidth="1.6" opacity="0.65">
                  {ARCS.map((d) => (
                    <path key={d} d={d} className="arc-flow" />
                  ))}
                </g>
                {/* Nodes */}
                {NODES.map((n) => (
                  <g
                    key={n.id}
                    onMouseEnter={() => setActive(n.id)}
                    onMouseLeave={() => setActive(null)}
                    onClick={() => setActive(n.id)}
                    data-testid={`map-node-${n.id}`}
                    className="cursor-pointer"
                  >
                    <circle cx={n.x} cy={n.y} r="10" fill="#C85A32" opacity="0.35" className="node-pulse" />
                    <circle cx={n.x} cy={n.y} r="22" fill="transparent" />
                    <circle
                      cx={n.x} cy={n.y} r={active === n.id ? 8 : 6}
                      fill="#C85A32" stroke="#FBF9F5" strokeWidth="2.5"
                      style={{ transition: "r 0.3s" }}
                    />
                    <text
                      x={n.x} y={n.y - 16} textAnchor="middle"
                      className="fill-ink font-mono text-[11px] font-semibold uppercase tracking-widest"
                    >
                      {n.name}
                    </text>
                  </g>
                ))}
              </svg>

              <AnimatePresence>
                {node && (
                  <motion.div
                    key={node.id}
                    initial={{ opacity: 0, y: 8, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 6, scale: 0.97 }}
                    transition={{ duration: 0.25 }}
                    className="pointer-events-none absolute z-10 w-52 -translate-x-1/2 rounded-xl border border-sand bg-white p-4 shadow-xl"
                    style={{
                      left: `clamp(112px, ${(node.x / 1000) * 100}%, calc(100% - 112px))`,
                      top: `${(node.y / 560) * 100}%`,
                      transform: "translate(-50%, -118%)",
                    }}
                    data-testid={`map-tooltip-${node.id}`}
                  >
                    <p className="font-serif text-lg text-ink">{node.flag} {node.name}</p>
                    <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.16em] text-terra">{node.hub}</p>
                    <p className="mt-2 text-xs leading-relaxed text-stone2">{node.note}</p>
                    <p className="mt-2 text-xs font-semibold text-ink">Native currency · {node.cur}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <p className="mt-4 text-center font-mono text-[11px] uppercase tracking-[0.18em] text-stone2">
              Hover a market to see how Travel-Tek supports it
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
